import {useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {refetchOffers, runSplitSearch} from 'modules/sapiSearch/actions'
import {useHandleSearchError, useSapi} from 'modules/sapiSearch/SapiProvider'
import {
  getCanRefreshOffers,
  getInputSearchParameters,
  getSapiPage,
  getSearchId
} from 'modules/sapiSearch/selectors'
import {
  offersRefetchFailed,
  offersRefetchStarted
} from 'modules/sapiSearch/slice'
import {scrollTo} from 'utils'
import {getAutoRefreshInterval} from 'utils/autoRefresh'

import {useActiveWindowTimeout} from '@vio/core/src/_web/utils/browser/hooks/useActiveWindowTimeout'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  Category,
  Entity,
  Page
} from '@vio/core/src/analytics/types/Events'
import {useToggle} from '@vio/core/src/experiments/hooks/useToggle'

import {useSearchPageRefreshToast} from './useSearchPageRefreshToast'

// Added as part of feature-srp-refresh-price
export const useAutoRefreshStaleSearches = () => {
  const toggle = useToggle()
  const dispatch = useDispatch()

  const handleSearchError = useHandleSearchError()
  const {sapiClient} = useSapi()

  const searchParameters = useSelector(getInputSearchParameters)
  const sapiPage = useSelector(getSapiPage)
  const searchId = useSelector(getSearchId)
  const canRefreshOffers = useSelector(getCanRefreshOffers)

  const canRefresh = !!sapiClient && canRefreshOffers

  const {closeFailedRefresh} = useSearchPageRefreshToast()

  const refresh = async () => {
    dispatch(
      trackEvent({
        category: Category.System,
        entity: Entity.PriceRefresh,
        action: Action.Displayed,
        page: Page.SearchResults
      })
    )
    dispatch(offersRefetchStarted())

    if (sapiPage === 0) {
      try {
        await dispatch(refetchOffers(searchParameters))
      } catch {
        // The traveller didn't ask for this refresh, so keep the old prices
        // instead of sending them to the error page
        closeFailedRefresh()
        dispatch(offersRefetchFailed())
      }
      return
    }

    scrollTo(document.querySelector('html'), 0, 0)

    // Past page 1, run a new search so we don't refresh only the last page
    try {
      await dispatch(
        runSplitSearch({searchParameters, hasPreloadedHotels: false})
      )
    } catch (error) {
      closeFailedRefresh()
      dispatch(offersRefetchFailed())
      handleSearchError(error)
    }
  }

  const resetTimer = useActiveWindowTimeout({
    callbackTimeoutMs: getAutoRefreshInterval(),
    callback: () => {
      if (!canRefresh) return

      // Checked here and not on render, so only travellers who reach a refresh join the test
      if (!toggle('feature-srp-refresh-price', false, true)) return

      refresh()
    }
  })

  // Changing dates or filters doesn't remount the page, so count from the latest search
  useEffect(() => {
    resetTimer()
  }, [searchId, resetTimer])
}
